import React, { useState, useEffect } from 'react';
import { Button, IconButton, Typography, Stack, Badge } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useCart } from '../context/CartContext';
import { Product } from '../types/api';

export interface AddToCartButtonProps {
  product: Product;
  variant?: 'text' | 'outlined' | 'contained';
  buttonSize?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  showIcon?: boolean;
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  product,
  variant = 'contained',
  buttonSize = 'medium',
  fullWidth = false,
  showIcon = true,
}) => {
  const { getCartItem, addToCart, updateQuantity, removeFromCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const cartItem = getCartItem(product.id);
  
  // Get max available quantity
  const maxQuantity = product.unlimited ? 999 : product.inStock && product.quantity ? product.quantity : 0;
  
  // Update local state when the cart item changes
  useEffect(() => {
    if (cartItem) {
      setQuantity(cartItem.quantity);
    } else {
      setQuantity(1);
    }
  }, [cartItem]);

  const handleIncrease = () => {
    const newQuantity = quantity + 1;
    
    // Check if new quantity exceeds available stock
    if (newQuantity > maxQuantity) return;
    
    if (cartItem) {
      updateQuantity(cartItem.id, newQuantity);
    }
  };

  const handleDecrease = () => {
    if (quantity > 1) {
      const newQuantity = quantity - 1;
      
      if (cartItem) {
        updateQuantity(cartItem.id, newQuantity);
      }
    } else if (cartItem) {
      // If quantity would become 0, remove from cart
      removeFromCart(cartItem.id);
    }
  };

  const handleAddToCart = () => {
    // Check if adding to cart would exceed available stock
    if (quantity > maxQuantity) return;
    
    addToCart(product, quantity);
  };

  // Button size mapping for Material UI
  const sizeMap: Record<'small' | 'medium' | 'large', {
    button: 'small' | 'medium' | 'large';
    iconButton: 'small' | 'medium' | 'large';
    typography: { fontSize: string };
    height: number;
    iconSize: 'small' | 'medium' | 'large';
  }> = {
    small: {
      button: 'small',
      iconButton: 'small',
      typography: { fontSize: '0.75rem' },
      height: 28,
      iconSize: 'small',
    },
    medium: {
      button: 'medium',
      iconButton: 'medium',
      typography: { fontSize: '0.875rem' },
      height: 36,
      iconSize: 'medium',
    },
    large: {
      button: 'large',
      iconButton: 'large',
      typography: { fontSize: '1rem' },
      height: 44,
      iconSize: 'large',
    },
  };

  const buttonStyles = sizeMap[buttonSize];

  // If out of stock, show disabled button
  if (maxQuantity <= 0) {
    return (
      <Button
        variant={variant}
        color="primary"
        fullWidth={fullWidth}
        size={buttonStyles.button}
        disabled
        sx={{
          height: buttonStyles.height,
          transition: 'all 0.2s ease-in-out',
        }}
      >
        Out of Stock
      </Button>
    );
  }

  // If product is in cart, show quantity controls
  if (cartItem) {
    return (
      <Stack 
        direction="row" 
        alignItems="center" 
        spacing={0.5}
        sx={{ 
          height: buttonStyles.height,
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 1,
          overflow: 'hidden',
          width: fullWidth ? '100%' : 'auto',
          display: 'inline-flex',
          bgcolor: 'rgba(21, 101, 74, 0.05)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <IconButton 
          onClick={handleDecrease} 
          size={buttonStyles.iconButton} 
          sx={{ 
            borderRadius: 0,
            height: '100%',
            color: 'primary.main',
            '&:hover': {
              bgcolor: 'rgba(21, 101, 74, 0.1)',
            },
          }}
        >
          <RemoveIcon fontSize={buttonStyles.iconSize} />
        </IconButton>
        
        <Typography 
          sx={{ 
            ...buttonStyles.typography,
            px: 1,
            minWidth: '32px',
            textAlign: 'center',
            color: 'primary.main',
            fontWeight: 'bold',
          }}
        >
          {cartItem.quantity}
        </Typography>
        
        <IconButton 
          onClick={handleIncrease} 
          size={buttonStyles.iconButton}
          disabled={cartItem.quantity >= maxQuantity}
          sx={{ 
            borderRadius: 0,
            height: '100%',
            color: 'primary.main',
            '&:hover': {
              bgcolor: 'rgba(21, 101, 74, 0.1)',
            },
          }}
        >
          <AddIcon fontSize={buttonStyles.iconSize} />
        </IconButton>
      </Stack>
    );
  }

  // If not in cart, show Add to Cart button
  return (
    <Button
      variant={variant}
      color="primary"
      fullWidth={fullWidth}
      size={buttonStyles.button}
      onClick={handleAddToCart}
      startIcon={showIcon ? <AddShoppingCartIcon /> : undefined}
      sx={{
        height: buttonStyles.height,
        transition: 'all 0.2s ease-in-out',
      }}
    >
      Add to cart
    </Button>
  );
};

export default AddToCartButton; 